import { queryOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';

import type { UserCards } from './types';

function getCustomersCards(customerId?: string) {
  if (!customerId) return Promise.resolve(null);
  return apiClient.get<UserCards>(`/v1/customer/${customerId}/payment-card`);
}

export const getCustomerCardsQuery = (
  customerId?: string,
  // Defaults to true so existing consumers (e.g. booking) keep the default
  // card first. Pass false where a stable API order matters — e.g. the card
  // management screen, so changing the default doesn't reorder the list.
  // NOTE: this only affects `select`, never the queryKey, so all consumers
  // share one cache entry and mutation invalidation keeps matching.
  { orderDefaultFirst = true }: { orderDefaultFirst?: boolean } = {},
) =>
  queryOptions({
    enabled: !!customerId,
    queryKey: ['customer', customerId, 'cards'],
    queryFn: () => getCustomersCards(customerId),
    select: (data) => {
      const cards = data?.data.map((card) => ({
        type: 'card',
        id: card.paymentCardId,
        card,
      }));

      if (!orderDefaultFirst) {
        return cards;
      }

      const defaultCardIdx =
        cards?.findIndex((method) => method.card.isDefault) ?? -1;

      if (defaultCardIdx < 0 || !cards?.length) {
        return cards;
      }

      // make default card first in the list
      return [
        cards[defaultCardIdx],
        ...cards.slice(0, defaultCardIdx),
        ...cards.slice(defaultCardIdx + 1),
      ];
    },
  });
